/**
 * semantic-search.js — Phase 2 (2026-08-01), v2 (2026-08-01), v3/v4 (2026-08-02), v5 (2026-08-13).
 *
 * Progressive enhancement on the /search results page: asks the app-proxy
 * endpoint (/apps/search → VPS sqlite-vec index) and, if it answers with
 * products, replaces the native grid with semantically-ranked results.
 *
 * FAIL-SOFT BY DESIGN: endpoint error, timeout (2.5s), or empty answer leaves
 * whatever the page already rendered untouched. Search can never get worse
 * than it was before this file existed.
 *
 * v2: PAGE 1 ONLY (page 2+ stays native, with real counts) · depth 48 · honest
 * count "Top 48 of 139 results" · native pagination left visible.
 * v3: colour swatches and quick-add, from the swatches / variantId fields.
 * v4: THE ZERO-RESULT PAGE. Previously this script gave up when Shopify's own
 *     search found nothing, because there was no grid to replace. Two problems
 *     with that, and the second is the one that mattered:
 *       · Shopify's search is LEXICAL and per-locale. "onduleur" on the English
 *         site matches no English document, so a francophone was told the store
 *         carries no UPS units while it carries dozens. Our index embeds EN+FR
 *         in one document per product, so the endpoint answers correctly — it
 *         simply was never asked. Symmetric: an English word on /fr too.
 *       · Those searches NEVER REACHED THE ENDPOINT, so they never reached
 *         search-log.db either. The lexical_hits = 0 miss queue was blind to
 *         precisely the queries most likely to be genuine misses, which would
 *         have put a hole in the data behind the 1 Sept phase-4 decision.
 *     Now: always ask, always log, and build a grid if there's an answer.
 * v5: THE IDENTIFIER GUARD. A barcode or SKU has no semantic meaning: the
 *     endpoint answers confident nonsense for one (UPC 199764290045 → headsets
 *     and transceivers), and that answer then REPLACED the native exact match.
 *     Identifier-shaped queries still go to the endpoint so search-log.db keeps
 *     seeing them — the 1 Sept phase-4 data depends on that — but they NEVER
 *     repaint: the native lexical result is authoritative for an identifier.
 *
 * ⚠ READ THIS BEFORE EDITING EITHER FILE ⚠
 * The cards built here are a HAND-BUILT COPY of snippets/product-card.liquid.
 * They are not generated from it and nothing keeps them in step. Anything added
 * to that snippet — a badge, a rating, a button — will silently fail to appear
 * in semantic search results until it is added here too. That drift is a known,
 * accepted cost; cloning a Liquid <template> was considered and rejected,
 * because the card computes discount percentages, branches on availability and
 * renders a delivery-estimator snippet, so the JSON would have to carry nearly
 * everything the Liquid computes anyway.
 * The colour→hex map below is duplicated from that snippet for the same reason:
 * Liquid cannot call JS and JS cannot read the Liquid map. Keep the two in the
 * same order — first match wins in both.
 *
 * RESPONSE CONTRACT (VPS, 2026-08-02) — three rules the bytes don't state:
 *  1. Keys are OMITTED, not null. Test presence. `variantId` and `swatches` are
 *     mutually exclusive: single-variant products get the former, colour
 *     products the latter.
 *  2. Images may be relative. Expand ONLY when the value doesn't already start
 *     with https:// — never concatenate unconditionally, because the full-URL
 *     fallback exists so a future CDN path change can't truncate silently.
 *  3. swatches[i][2] (the variant id) is NAVIGATION-ONLY. For the 81 products
 *     where a colour value repeats across sizes it is a first match standing
 *     for several real variants, so it may deep-link the product page and must
 *     NEVER be added to a cart.
 */
(function () {
  'use strict';

  var qs = new URLSearchParams(location.search);
  var q  = (qs.get('q') || '').trim();
  if (q.length < 2) return;
  if ((parseInt(qs.get('page') || '1', 10) || 1) > 1) return; // page 1 only

  /* v5 — identifier shapes: pure 8-14 digit numerics (UPC/EAN/GTIN), or a
     spaceless token of 5+ chars carrying 2+ digits (FK8651, DH-USB3SAB10BKCN).
     Fetch still happens (logging); repaint never does. */
  var IDENTIFIER = /^\d{8,14}$/.test(q) ||
                   (q.indexOf(' ') === -1 && q.length >= 5 && q.replace(/\D/g, '').length >= 2);

  var locale = location.pathname.indexOf('/fr') === 0 ? 'fr' : 'en';
  var page   = document.querySelector('.search-page .container');
  var grid   = document.querySelector('[data-search-results], .search-page .product-grid');

  // v4: no grid is no longer a reason to stand down — it's the zero-result page.
  var zeroMode = !grid;
  if (!grid && !page) return;   // not a search template we recognise

  var money = function (v) {
    var n = Number(v).toFixed(2);
    if (locale === 'fr') return n.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' $';
    return '$' + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  var sized = function (u, w) {
    if (!u) return null;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  };

  /* Mirrors the map in snippets/product-card.liquid — same order, first match
     wins. Unrecognised values stay grey, which is correct: the vendor feed
     contains compound values ("Black White") and at least one typo ("Walunt").
     The image swap never reads the name, so those still preview the right
     photo even though the dot is grey. */
  function swatchColour(value) {
    var c = (value || '').toLowerCase();
    var has = function (s) { return c.indexOf(s) > -1; };
    var edge = 'rgba(0,0,0,.15)';
    if (has('black') || has('noir')) return ['#1f2937', edge];
    if (has('white') || has('cream') || has('ivory') || has('blanc')) return ['#f9f9f7', 'rgba(0,0,0,.3)'];
    if (has('grey') || has('gray') || has('charcoal') || has('gris')) return ['#6b7280', edge];
    if (has('brown') || has('walnut') || has('oak') || has('wood') || has('coffee') || has('brun')) return ['#8b5a2b', edge];
    if (has('beige') || has('khaki') || has('tan') || has('natural') || has('naturel')) return ['#d9c7a7', edge];
    if (has('teal') || has('turquoise')) return ['#0d9488', edge];
    if (has('blue') || has('navy') || has('bleu')) return ['#2563eb', edge];
    if (has('green') || has('vert')) return ['#16a34a', edge];
    if (has('red') || has('burgundy') || has('wine') || has('rouge')) return ['#dc2626', edge];
    if (has('pink') || has('rose')) return ['#ec4899', edge];
    if (has('yellow') || has('jaune')) return ['#eab308', edge];
    if (has('orange')) return ['#f97316', edge];
    if (has('purple') || has('violet')) return ['#7c3aed', edge];
    if (has('silver') || has('argent')) return ['#cbd5e1', edge];
    if (has('gold') || has('or ')) return ['#d4af37', edge];
    return ['#9ca3af', edge];
  }

  var controller = new AbortController();
  var timer = setTimeout(function () { controller.abort(); }, 2500);

  fetch('/apps/search?q=' + encodeURIComponent(q) + '&locale=' + locale + '&limit=48',
        { signal: controller.signal, headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      clearTimeout(timer);
      // v5: the request above is the point for identifiers — the query is now
      // logged. The native exact match stands; semantic ranking has nothing to
      // offer a barcode. Bail BEFORE any DOM work.
      if (IDENTIFIER) return;
      // The request itself is the point on the zero-result page: even with no
      // answer, the query is now logged. Bail only after that has happened.
      if (!data || !data.products || data.products.length === 0) return;

      var prefix = data.imagePrefix || '';
      // Rule 2: expand only when the value isn't already absolute.
      var expand = function (u) {
        if (!u) return null;
        return u.indexOf('https://') === 0 ? u : prefix + u;
      };

      var count = document.querySelector('[data-search-count]');
      var total = 0;
      if (!zeroMode) {
        // Read the native total BEFORE repainting the count element.
        var totalMatch = (count && count.textContent || '').replace(/ /g, ' ').match(/\d[\d\s.,]*/);
        total = totalMatch ? parseInt(totalMatch[0].replace(/[^\d]/g, ''), 10) : 0;
      } else {
        /* Build the grid the zero-result template never rendered, and stand the
           older rescue block down. The attribute is REMOVED rather than just
           hidden: predictive-search.js is deferred too, so it may still be in
           flight, and removing its hook stops it filling a block nobody will
           see. */
        var rescue = page.querySelector('[data-psearch-rescue]');
        var empty  = page.querySelector('.empty-state');
        var anchor = rescue || empty;
        if (!anchor) return;

        var zeroLine = rescue && rescue.previousElementSibling &&
                       rescue.previousElementSibling.tagName === 'P'
                     ? rescue.previousElementSibling : null;

        count = document.createElement('p');
        count.setAttribute('data-search-count', '');
        count.style.cssText = 'color:var(--color-text-light);margin-bottom:2rem;';

        grid = document.createElement('div');
        grid.className = 'product-grid';
        grid.setAttribute('data-search-results', '');

        page.insertBefore(count, anchor);
        page.insertBefore(grid, anchor);

        if (zeroLine) zeroLine.style.display = 'none';
        if (empty)    empty.style.display = 'none';
        if (rescue) {
          rescue.removeAttribute('data-psearch-rescue');
          rescue.hidden = true;
          rescue.style.display = 'none';
        }
      }

      var frag = document.createDocumentFragment();

      data.products.forEach(function (p) {
        var wrap = document.createElement('div');
        wrap.className = 'product-card-wrap';

        var img = sized(expand(p.image), 400);
        var onSale = p.compareAt && p.compareAt > p.price;

        wrap.innerHTML =
          '<a class="product-card">' +
            '<div class="product-card-image">' +
              (img
                ? '<img loading="lazy" decoding="async" width="400" height="400" alt="">'
                : '<div style="width:100%;height:100%;background:var(--color-bg-secondary);display:flex;align-items:center;justify-content:center;color:#ccc;"><i class="fa-regular fa-image" style="font-size:2rem;"></i></div>') +
            '</div>' +
            '<div class="product-card-body">' +
              '<h3 class="product-card-title"></h3>' +
              '<div class="product-card-prices">' +
                '<span class="product-card-price">' + money(p.price) + '</span>' +
                (onSale ? '<span class="product-card-compare-price">' + money(p.compareAt) + '</span>' : '') +
              '</div>' +
            '</div>' +
          '</a>';

        var a = wrap.querySelector('a');
        a.href = p.url;                                    // property assignment — no HTML injection
        wrap.querySelector('.product-card-title').textContent = p.title;
        var imgEl = wrap.querySelector('img');
        if (imgEl) { imgEl.src = img; imgEl.alt = p.title; }

        var body = wrap.querySelector('.product-card-body');
        var prices = wrap.querySelector('.product-card-prices');

        /* Colour swatches — inserted BEFORE the price block, matching the order
           in product-card.liquid. data-sw-img and data-sw-variant are what
           assets/swatch-preview.js listens for; because that handler is
           delegated at the document level, these cards inherit hover-to-preview
           and click-to-that-colour with no extra code here. */
        if (p.swatches && p.swatches.length) {
          var row = document.createElement('div');
          row.className = 'product-card-swatches';
          row.style.cssText = 'display:flex;align-items:center;gap:4px;margin:2px 0 4px;';
          row.setAttribute('aria-label', p.swatches.length + (locale === 'fr' ? ' couleurs' : ' colours'));

          p.swatches.slice(0, 5).forEach(function (sw) {
            var value = sw[0], swImg = expand(sw[1]), variantId = sw[2];
            var pair = swatchColour(value);
            var dot = document.createElement('span');
            dot.title = value;
            dot.style.cssText = 'width:13px;height:13px;border-radius:50%;background:' + pair[0] +
                                ';border:1px solid ' + pair[1] + ';display:inline-block;flex-shrink:0;' +
                                ((swImg || variantId) ? 'cursor:pointer;' : '');
            if (swImg) dot.setAttribute('data-sw-img', sized(swImg, 400));
            // Rule 3: navigation only. Never wire this into an add-to-cart form.
            if (variantId) dot.setAttribute('data-sw-variant', variantId);
            row.appendChild(dot);
          });

          if (p.swatches.length > 5) {
            var more = document.createElement('span');
            more.style.cssText = 'font-size:.72rem;color:#6b7280;line-height:1;';
            more.textContent = '+' + (p.swatches.length - 5);
            row.appendChild(more);
          }
          body.insertBefore(row, prices);
        }

        /* Quick-add — single-variant products only (92% of the catalogue).
           Colour products deliberately get no button: their variant id is a
           first match and would add the wrong size. Those fall through to the
           product page, which is the correct destination anyway. */
        if (p.variantId && p.available) {
          var form = document.createElement('form');
          form.method = 'post';
          form.action = '/cart/add';
          form.className = 'product-card-form';
          form.innerHTML =
            '<input type="hidden" name="quantity" value="1">' +
            '<button type="submit" class="product-card-atc">' +
              '<i class="fa-solid fa-cart-shopping"></i> ' +
              (locale === 'fr' ? 'Ajouter au panier' : 'Add to cart') +
            '</button>';
          var idInput = document.createElement('input');
          idInput.type = 'hidden'; idInput.name = 'id'; idInput.value = p.variantId;
          form.insertBefore(idInput, form.firstChild);
          body.appendChild(form);
        } else if (p.available === false) {
          var oos = document.createElement('p');
          oos.style.cssText = 'font-size:.8rem;color:var(--color-text-light);margin:.25rem 0 0;';
          oos.textContent = locale === 'fr' ? 'Rupture de stock' : 'Out of stock';
          body.appendChild(oos);
        }

        frag.appendChild(wrap);
      });

      grid.innerHTML = '';
      grid.appendChild(frag);
      grid.setAttribute('data-semantic', 'true');

      if (count) {
        var n = data.products.length;
        if (zeroMode) {
          /* Native search found nothing, so there is no honest "of N" to show.
             These are real products the store carries — the customer's word
             simply isn't in this locale's text — so they're presented as
             results, not as apologies. */
          count.textContent = locale === 'fr'
            ? n + ' résultats pertinents'
            : n + ' relevant results';
        } else if (total > n) {
          count.textContent = locale === 'fr'
            ? n + ' meilleurs résultats sur ' + total
            : 'Top ' + n + ' of ' + total + ' results';
        } else {
          count.textContent = locale === 'fr'
            ? n + ' résultats pertinents'
            : n + ' relevant results';
        }
      }
    })
    .catch(function () { clearTimeout(timer); /* fail soft — the page stands as rendered */ });
})();